import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import {
  activeChat,
  CenterlineAiAction,
  CenterlineAiState,
  chatReducer,
  findTurn,
  initialCenterlineAiState,
} from './core/chat-reducer';
import {
  chatFailureText,
  createQueryToolClient,
  QueryToolClient,
  QueryToolHttpError,
  SERVER_ID_RE,
} from './core/query-tool-client';
import { createTokenCache, IssuedToken } from './core/token-cache';
import { isAbortError } from './core/sse';
import {
  ChatContextScope,
  ChatRequest,
  ContextFile,
  FeedbackReason,
  FileStatus,
  SseFrame,
} from './core/types';
import {
  contextPayload,
  EMPTY_SELECTION,
  filesCacheKey,
  FILE_LIMIT,
} from './core/context-selection';
import {
  browserStorage,
  resolveRunOptions,
  RunOptionKind,
  runOptionsPayload,
  saveRunOption,
} from './core/run-options';
import { isPickable } from './core/tools';
import { renamedTitle } from './core/titles';
import { DocumentTypeSlugs } from './core/citations';

/**
 * Runs the chat page: the reducer, the Query Tool client and every effect around them (loading
 * projects, sessions, transcripts, the "@" lists and the run-options roster; sending and stopping
 * turns; fire-and-forget rename, delete and feedback). Components read state and call actions
 * through `useCenterlineAi()`.
 */

export interface LiveProject {
  id: string;
  name: string;
}

export interface ProjectsState {
  status: 'loading' | 'ready' | 'failed';
  list: LiveProject[];
}

/**
 * A turn the page prepared rather than the user typed (the document review): `message` is sent,
 * `displayMessage` is what the thread shows, and `scope` and `options` replace the "@" selection
 * and the run options for this turn only.
 */
export interface PreparedTurn {
  message: string;
  displayMessage: string;
  scope?: ChatContextScope;
  tool?: string | null;
  options?: Partial<Record<RunOptionKind, string>>;
}

export interface CenterlineAiActions {
  selectProject(projectId: string): void;
  newChat(): void;
  openChat(localId: number): void;
  renameChat(localId: number, title: string): void;
  deleteChat(localId: number): void;
  toggleRail(): void;
  send(question: string): void;
  sendPrepared(turn: PreparedTurn): void;
  /** Opens a stored session of the selected project; resolves false when it has no turns. */
  restoreSession(sessionId: string): Promise<boolean>;
  /** Where files stand in the Query Tool's ingestion; rejects when it cannot say. */
  fileStatus(fileIds: string[]): Promise<FileStatus[]>;
  stop(): void;
  setTool(tool: string | null): void;
  rate(chatId: number, turnKey: string, rating: 1 | -1): void;
  chooseReason(chatId: number, turnKey: string, reason: FeedbackReason): void;
  saveNote(chatId: number, turnKey: string, note: string): void;
  openPicker(): void;
  closePicker(): void;
  loadFiles(docType: string, term: string): void;
  setDocFilter(docType: string): void;
  toggleType(docType: string): void;
  toggleFile(file: ContextFile): void;
  removeType(docType: string): void;
  removeFile(fileId: string): void;
  clearContext(): void;
  setRunOption(kind: RunOptionKind, value: string): void;
}

export interface CenterlineAiContextValue {
  state: CenterlineAiState;
  projects: ProjectsState;
  actions: CenterlineAiActions;
  slugs: DocumentTypeSlugs;
  /**
   * The route names a project that isn't in the user's live list. The page says so rather than
   * falling back to another project.
   */
  routeUnavailable: boolean;
  /** The composer's textarea, refocused when a turn ends. */
  inputRef: React.MutableRefObject<HTMLTextAreaElement | null>;
  /** Whether the composer offers the "+" tools menu (site admins only; the page decides). */
  showTools: boolean;
  /** The review pane: no "@" picker or tools, and every question keeps the review's scope. */
  compact: boolean;
}

const CenterlineAiContext = createContext<CenterlineAiContextValue | null>(null);

export const useCenterlineAi = (): CenterlineAiContextValue => {
  const value = useContext(CenterlineAiContext);
  if (!value) throw new Error('useCenterlineAi must be used inside CenterlineAiProvider');
  return value;
};

export const MISMATCH_TEXT =
  'The answer came back for a different project than the one asked about, so it was stopped.';

interface Props {
  baseUrl: string;
  /**
   * The project in the route. When it is set, it is the only project the chat starts on: if the
   * user may not ask about it, nothing is selected (`routeUnavailable`).
   */
  routeProjectId?: string | null;
  /** The project open in CENTERLINE, used when the route names none. */
  currentProjectId?: string | null;
  /** Called after the user picks a project, so the page can move the route to it. */
  onProjectChosen?(projectId: string): void;
  /** Called whenever the selected project changes: at start, on a pick, or as the route moves. */
  onProjectSelected?(projectId: string): void;
  issueToken(projectId: string): Promise<IssuedToken>;
  listProjects(): Promise<LiveProject[]>;
  slugs: DocumentTypeSlugs;
  /** Whether the composer shows the "+" tools button. Off unless the page says the user may. */
  showTools?: boolean;
  /**
   * The document review pane: the composer drops the "@" picker and the tools menu, and
   * `fixedScope` (when set) is the scope of every question, in place of the "@" selection.
   */
  compact?: boolean;
  fixedScope?: ChatContextScope;
  /** Tests pass a fake client; otherwise one is built over `fetch`. */
  client?: QueryToolClient;
  children: React.ReactNode;
}

export function CenterlineAiProvider(props: Props) {
  const {
    baseUrl,
    routeProjectId,
    currentProjectId,
    onProjectChosen,
    onProjectSelected,
    issueToken,
    listProjects,
    slugs,
    showTools = false,
    compact = false,
    fixedScope,
  } = props;
  const [state, rawDispatch] = useReducer(chatReducer, undefined, initialCenterlineAiState);
  const [projects, setProjects] = useState<ProjectsState>({ status: 'loading', list: [] });

  const mounted = useRef(true);
  const stateRef = useRef(state);
  stateRef.current = state;
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const turnCounter = useRef(0);
  const docTypesSeq = useRef(0);
  const filesInFlight = useRef(new Set<string>());

  const dispatch = useCallback((action: CenterlineAiAction) => {
    if (mounted.current) rawDispatch(action);
  }, []);

  const client = useMemo(() => {
    if (props.client) return props.client;
    const tokens = createTokenCache(issueToken);
    return createQueryToolClient({ baseUrl, getToken: tokens.get });
    // The client lives as long as the page; its inputs are fixed for the build.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () => () => {
      mounted.current = false;
      controllerRef.current?.abort();
    },
    [],
  );

  /* ---- deltas are painted at most once per animation frame (§5.9) ---- */
  const pendingDelta = useRef<{ chatId: number; turnKey: string; text: string } | null>(null);
  const deltaFrame = useRef<number | null>(null);

  const flushDelta = useCallback(() => {
    if (deltaFrame.current !== null) {
      cancelAnimationFrame(deltaFrame.current);
      deltaFrame.current = null;
    }
    const pending = pendingDelta.current;
    pendingDelta.current = null;
    if (pending && pending.text) dispatch({ type: 'delta', ...pending });
  }, [dispatch]);

  const queueDelta = useCallback(
    (chatId: number, turnKey: string, text: string) => {
      const pending = pendingDelta.current;
      if (pending && pending.chatId === chatId && pending.turnKey === turnKey) {
        pending.text += text;
      } else {
        flushDelta();
        pendingDelta.current = { chatId, turnKey, text };
      }
      if (deltaFrame.current === null) {
        deltaFrame.current = requestAnimationFrame(() => {
          deltaFrame.current = null;
          flushDelta();
        });
      }
    },
    [flushDelta],
  );

  /* ---- projects: CENTERLINE's live projects for this user ---- */
  useEffect(() => {
    let current = true;
    listProjects()
      .then((list) => {
        if (!current) return;
        const live = (list ?? []).filter((p) => p && typeof p.id === 'string');
        setProjects({ status: 'ready', list: live });
        const has = (id?: string | null) => !!id && live.some((p) => p.id === id);
        const initial = routeProjectId
          ? has(routeProjectId)
            ? routeProjectId
            : undefined
          : has(currentProjectId)
          ? currentProjectId
          : live[0]?.id;
        if (initial) dispatch({ type: 'projectSelected', projectId: initial });
      })
      .catch(() => current && setProjects({ status: 'failed', list: [] }));
    return () => {
      current = false;
    };
    // Loaded once per visit, like the original's start-up chain (§0).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---- the route moved to another project (back/forward): follow it ---- */
  useEffect(() => {
    if (projects.status !== 'ready' || !routeProjectId) return;
    if (routeProjectId === stateRef.current.projectId) return;
    if (projects.list.some((p) => p.id === routeProjectId)) {
      dispatch({ type: 'projectSelected', projectId: routeProjectId });
    }
  }, [routeProjectId, projects, dispatch]);

  /* ---- on a project change: tell the page, then the sidebar list, and the roster once ---- */
  const selectedProjectId = state.projectId;
  const onProjectSelectedRef = useRef(onProjectSelected);
  onProjectSelectedRef.current = onProjectSelected;
  useEffect(() => {
    if (selectedProjectId) onProjectSelectedRef.current?.(selectedProjectId);
  }, [selectedProjectId]);
  useEffect(() => {
    const projectId = selectedProjectId;
    if (!projectId) return;
    client
      .listSessions(projectId)
      .then((sessions) => dispatch({ type: 'sessionsLoaded', projectId, sessions }))
      .catch(() => undefined);
    if (stateRef.current.runOptions.status === 'idle') {
      dispatch({ type: 'rosterLoading' });
      client
        .getModels(projectId)
        .then((roster) =>
          dispatch({
            type: 'rosterLoaded',
            roster,
            options: resolveRunOptions(roster, browserStorage()),
          }),
        )
        .catch(() => dispatch({ type: 'rosterFailed' }));
    }
  }, [selectedProjectId, client, dispatch]);

  /* ---- sending and stopping ---- */
  const runTurn = useCallback(
    async (question: string, prepared?: PreparedTurn) => {
      const s = stateRef.current;
      const chat = activeChat(s);
      const message = question.trim();
      if (s.inFlight || !s.projectId || !chat || !message) return;
      turnCounter.current += 1;
      const turnKey = `t${turnCounter.current}`;
      const chatId = chat.localId;
      const projectId = s.projectId;
      const scope = prepared ? prepared.scope : fixedScope ?? contextPayload(s.context.selection);
      const tool = prepared
        ? prepared.tool ?? null
        : isPickable(s.activeTool)
        ? s.activeTool
        : null;
      const body: ChatRequest = {
        message,
        project_id: projectId,
        // A session from another project would be replaced by the server anyway (contract §9.3).
        session_id:
          chat.sessionId && chat.projectId === projectId && SERVER_ID_RE.test(chat.sessionId)
            ? chat.sessionId
            : null,
        tool,
        ...(scope ? { context: scope } : {}),
        ...runOptionsPayload(s.runOptions),
        ...(prepared ? prepared.options : {}),
      };
      dispatch({
        type: 'send',
        chatId,
        turnKey,
        question: prepared ? prepared.displayMessage : message,
        tool: body.tool,
        // A fixed or prepared scope is shown by the page itself, not as chips under the question.
        context: prepared || fixedScope ? EMPTY_SELECTION : s.context.selection,
      });

      const controller = new AbortController();
      controllerRef.current = controller;
      let mismatch = false;
      const onFrame = (frame: SseFrame) => {
        if (mismatch) return;
        if (frame.event === 'start') {
          const echoed = typeof frame.data.projectId === 'string' ? frame.data.projectId : '';
          if (echoed && echoed !== projectId) {
            mismatch = true;
            flushDelta();
            dispatch({ type: 'turnFailed', chatId, turnKey, message: MISMATCH_TEXT });
            controller.abort();
            return;
          }
        }
        if (frame.event === 'delta') {
          const text = typeof frame.data.text === 'string' ? frame.data.text : '';
          if (text) queueDelta(chatId, turnKey, text);
          return;
        }
        flushDelta();
        dispatch({ type: 'frame', chatId, turnKey, frame });
      };
      try {
        await client.streamChat(body, { onFrame }, controller.signal);
      } catch (err) {
        flushDelta();
        if (isAbortError(err)) {
          if (!mismatch) dispatch({ type: 'turnStopped', chatId, turnKey });
        } else {
          dispatch({ type: 'turnFailed', chatId, turnKey, message: chatFailureText(err) });
        }
      } finally {
        flushDelta();
        if (controllerRef.current === controller) controllerRef.current = null;
        dispatch({ type: 'turnSettled', chatId, turnKey });
        if (mounted.current) setTimeout(() => inputRef.current?.focus(), 0);
      }
    },
    [client, dispatch, flushDelta, queueDelta, fixedScope],
  );

  const send = useCallback((question: string) => runTurn(question), [runTurn]);

  const sendPrepared = useCallback((turn: PreparedTurn) => runTurn(turn.message, turn), [runTurn]);

  const restoreSession = useCallback(
    async (sessionId: string): Promise<boolean> => {
      const projectId = stateRef.current.projectId;
      if (!projectId) return false;
      try {
        const turns = await client.getSession(projectId, sessionId);
        if (!turns.length) return false;
        dispatch({ type: 'sessionRestored', projectId, sessionId, turns });
        return true;
      } catch {
        return false;
      }
    },
    [client, dispatch],
  );

  const fileStatus = useCallback(
    (fileIds: string[]) => {
      const projectId = stateRef.current.projectId;
      if (!projectId) return Promise.reject(new Error('No project selected.'));
      return client.getFileStatus(projectId, fileIds);
    },
    [client],
  );

  const stop = useCallback(() => controllerRef.current?.abort(), []);

  /* ---- sidebar ---- */
  const openChat = useCallback(
    (localId: number) => {
      const s = stateRef.current;
      const chat = s.chats.find((c) => c.localId === localId);
      if (!chat) return;
      dispatch({ type: 'openChat', localId });
      const projectId = chat.projectId ?? s.projectId;
      if (chat.sessionId && !chat.loaded && projectId) {
        dispatch({ type: 'transcriptRequested', localId });
        client
          .getSession(projectId, chat.sessionId)
          .then((turns) => dispatch({ type: 'transcriptLoaded', localId, turns }))
          .catch(() => undefined);
      }
    },
    [client, dispatch],
  );

  const renameChat = useCallback(
    (localId: number, title: string) => {
      const s = stateRef.current;
      const chat = s.chats.find((c) => c.localId === localId);
      const name = renamedTitle(title);
      if (!chat || name === null) return;
      dispatch({ type: 'renameChat', localId, title: name });
      const projectId = chat.projectId ?? s.projectId;
      if (chat.sessionId && projectId) {
        client.renameSession(projectId, chat.sessionId, name).catch(() => undefined);
      }
    },
    [client, dispatch],
  );

  const deleteChat = useCallback(
    (localId: number) => {
      const s = stateRef.current;
      const chat = s.chats.find((c) => c.localId === localId);
      if (!chat) return;
      const projectId = chat.projectId ?? s.projectId;
      if (chat.sessionId && projectId) {
        client.archiveSession(projectId, chat.sessionId).catch(() => undefined);
      }
      dispatch({ type: 'deleteChat', localId });
    },
    [client, dispatch],
  );

  /* ---- feedback: every change posts at once, fire-and-forget (§8) ---- */
  const postFeedback = useCallback(
    (
      chatId: number,
      turnKey: string,
      body: { rating: 1 | -1; reason?: FeedbackReason; note?: string },
    ) => {
      const s = stateRef.current;
      const found = findTurn(s, chatId, turnKey);
      const turnId = found?.turn.bot.turnId;
      const projectId = found?.turn.projectId ?? found?.chat.projectId ?? s.projectId;
      if (!turnId || !projectId) return;
      client.sendFeedback(projectId, { turn_id: turnId, ...body }).catch(() => undefined);
    },
    [client],
  );

  const rate = useCallback(
    (chatId: number, turnKey: string, rating: 1 | -1) => {
      dispatch({ type: 'feedback', chatId, turnKey, patch: { rating } });
      postFeedback(chatId, turnKey, { rating });
    },
    [dispatch, postFeedback],
  );

  const chooseReason = useCallback(
    (chatId: number, turnKey: string, reason: FeedbackReason) => {
      dispatch({ type: 'feedback', chatId, turnKey, patch: { rating: -1, reason } });
      postFeedback(chatId, turnKey, { rating: -1, reason });
    },
    [dispatch, postFeedback],
  );

  const saveNote = useCallback(
    (chatId: number, turnKey: string, note: string) => {
      const trimmed = note.trim().slice(0, 2000);
      dispatch({ type: 'feedback', chatId, turnKey, patch: { note } });
      const rating = findTurn(stateRef.current, chatId, turnKey)?.turn.bot.feedback.rating;
      if (trimmed && rating) postFeedback(chatId, turnKey, { rating, note: trimmed });
    },
    [dispatch, postFeedback],
  );

  /* ---- "@" picker lists (§4.4) ---- */
  const loadDocTypes = useCallback(() => {
    const s = stateRef.current;
    const { projectId } = s;
    if (!projectId || s.context.docTypesFor === projectId) return;
    docTypesSeq.current += 1;
    const seq = docTypesSeq.current;
    client
      .getDocTypes(projectId)
      .then((docTypes) => {
        if (seq === docTypesSeq.current)
          dispatch({ type: 'ctxDocTypesLoaded', projectId, docTypes, cache: true });
      })
      .catch((err) => {
        // A reply the server sent is cached as empty; a thrown error is retried on the next open.
        if (seq === docTypesSeq.current)
          dispatch({
            type: 'ctxDocTypesLoaded',
            projectId,
            docTypes: [],
            cache: err instanceof QueryToolHttpError,
          });
      });
  }, [client, dispatch]);

  const loadFiles = useCallback(
    (docType: string, term: string) => {
      const s = stateRef.current;
      const { projectId } = s;
      if (!projectId) return;
      const key = filesCacheKey(docType, term);
      const flightKey = `${projectId}\u0000${key}`;
      if (s.context.fileCache[key] || filesInFlight.current.has(flightKey)) return;
      filesInFlight.current.add(flightKey);
      client
        .getContextFiles(projectId, { docType, q: term, limit: FILE_LIMIT })
        .catch(() => [])
        .then((files) => dispatch({ type: 'ctxFilesLoaded', projectId, key, files }))
        .finally(() => filesInFlight.current.delete(flightKey));
    },
    [client, dispatch],
  );

  const openPicker = useCallback(() => {
    dispatch({ type: 'ctxPicker', open: true });
    loadDocTypes();
  }, [dispatch, loadDocTypes]);

  const actions = useMemo<CenterlineAiActions>(
    () => ({
      selectProject: (projectId) => {
        dispatch({ type: 'projectSelected', projectId });
        onProjectChosen?.(projectId);
      },
      newChat: () => dispatch({ type: 'newChat' }),
      openChat,
      renameChat,
      deleteChat,
      toggleRail: () => dispatch({ type: 'toggleRail' }),
      send: (question) => {
        send(question);
      },
      sendPrepared: (turn) => {
        sendPrepared(turn);
      },
      restoreSession,
      fileStatus,
      stop,
      setTool: (tool) => dispatch({ type: 'setTool', tool }),
      rate,
      chooseReason,
      saveNote,
      openPicker,
      closePicker: () => dispatch({ type: 'ctxPicker', open: false }),
      loadFiles,
      setDocFilter: (docType) => dispatch({ type: 'ctxFilter', docType }),
      toggleType: (docType) => dispatch({ type: 'ctxToggleType', docType }),
      toggleFile: (file) => dispatch({ type: 'ctxToggleFile', file }),
      removeType: (docType) => dispatch({ type: 'ctxRemoveType', docType }),
      removeFile: (fileId) => dispatch({ type: 'ctxRemoveFile', fileId }),
      clearContext: () => dispatch({ type: 'ctxClear' }),
      setRunOption: (kind, value) => {
        dispatch({ type: 'setRunOption', kind, value });
        saveRunOption(browserStorage(), kind, value);
      },
    }),
    [
      dispatch,
      onProjectChosen,
      openChat,
      renameChat,
      deleteChat,
      send,
      sendPrepared,
      restoreSession,
      fileStatus,
      stop,
      rate,
      chooseReason,
      saveNote,
      openPicker,
      loadFiles,
    ],
  );

  const routeUnavailable =
    projects.status === 'ready' &&
    !!routeProjectId &&
    !projects.list.some((p) => p.id === routeProjectId);

  const value = useMemo(
    () => ({ state, projects, actions, slugs, routeUnavailable, inputRef, showTools, compact }),
    [state, projects, actions, slugs, routeUnavailable, showTools, compact],
  );

  return (
    <CenterlineAiContext.Provider value={value}>{props.children}</CenterlineAiContext.Provider>
  );
}
